// client/src/components/media/VideoEditor.jsx
// In-app video editor with PWA-standard workflow:
//   Trim (set start / end points)
//   Text overlay layers (drag to position, style, @mention support)
//   Closed Captions / Subtitles (WebVTT authoring)
//   Filters (brightness / contrast / saturation / warmth)
// Mirrors MediaEditor pattern: fullscreen overlay, uploads internally.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaTimes, FaCheck, FaCut, FaFont, FaClosedCaptioning, FaSun,
  FaPlus, FaTrash, FaBold, FaItalic, FaArrowLeft,
  FaPlay, FaPause, FaStepBackward, FaStepForward,
} from 'react-icons/fa';
import MentionInput from './MentionInput';
import './VideoEditor.css';

// ── filter helpers (same as MediaEditor) ────────────────────────────────────
function buildCSSFilter(adj) {
  const parts = [];
  if (adj.brightness !== undefined && adj.brightness !== 1) parts.push(`brightness(${adj.brightness})`);
  if (adj.contrast   !== undefined && adj.contrast !== 1)   parts.push(`contrast(${adj.contrast})`);
  if (adj.saturate   !== undefined && adj.saturate !== 1)    parts.push(`saturate(${adj.saturate})`);
  if (adj.sepia      !== undefined && adj.sepia !== 0)       parts.push(`sepia(${adj.sepia})`);
  return parts.length ? parts.join(' ') : 'none';
}

// ── time formatting ─────────────────────────────────────────────────────────
function fmt(sec) {
  if (!sec || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec % 1) * 100);
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}.${String(ms).padStart(2,'0')}`;
}
function fmtVTT(sec) {
  if (!sec || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec % 1) * 1000);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}.${String(ms).padStart(3,'0')}`;
}
function parseVTTTime(str) {
  const parts = str.split(':');
  let h = 0, m = 0, s = 0;
  if (parts.length === 3) { h = +parts[0]; m = +parts[1]; s = parseFloat(parts[2]); }
  else if (parts.length === 2) { m = +parts[0]; s = parseFloat(parts[1]); }
  else { s = parseFloat(parts[0]); }
  return h * 3600 + m * 60 + s;
}

function generateVTT(cues) {
  let vtt = 'WEBVTT\n\n';
  cues.forEach((c, i) => {
    vtt += `${i+1}\n${fmtVTT(c.start)} --> ${fmtVTT(c.end)}\n${c.text}\n\n`;
  });
  return vtt;
}

function parseVTT(text) {
  const lines = text.split('\n');
  const parsed = [];
  let i = 0;
  while (i < lines.length && !lines[i].includes('-->')) i++;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (line.includes('-->')) {
      const [s, e] = line.split('-->').map(x => x.trim());
      i++;
      let tLines = [];
      while (i < lines.length && lines[i].trim() !== '') { tLines.push(lines[i].trim()); i++; }
      parsed.push({ id: Date.now() + parsed.length, start: parseVTTTime(s), end: parseVTTTime(e), text: tLines.join('\n') });
    }
    i++;
  }
  return parsed;
}

const TABS = ['trim', 'text', 'captions', 'adjust'];

// ─────────────────────────────────────────────────────────────────────────────
export default function VideoEditor({
  src,
  tenantId,
  eventId = null,
  itemId = null,
  existingVttUrl = null,
  onSaved,
  onClose,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animRef = useRef(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('trim');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [dispSize, setDispSize] = useState({ w: 0, h: 0 });

  // ── Trim state ─────────────────────────────────────────────────────────────
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  // ── Filter / adjust state ──────────────────────────────────────────────────
  const [adj, setAdj] = useState({ brightness: 1, contrast: 1, saturate: 1, sepia: 0 });
  const updateAdj = (k, v) => setAdj(p => ({ ...p, [k]: v }));

  // ── Text overlay state ─────────────────────────────────────────────────────
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [draggingLayer, setDraggingLayer] = useState(null);
  const activeLayer = layers.find(l => l.id === activeLayerId);

  const addTextLayer = () => {
    const id = Date.now();
    setLayers(prev => [...prev, {
      id, text: 'Your text', x: 20, y: 20 + prev.length * 50,
      fontSize: 28, color: '#ffffff', bgColor: 'rgba(0,0,0,0.5)',
      bold: false, italic: false,
      showStart: trimStart, showEnd: trimEnd || duration,
    }]);
    setActiveLayerId(id);
  };
  const updateLayer = (id, k, v) => setLayers(p => p.map(l => l.id === id ? { ...l, [k]: v } : l));
  const removeLayer = (id) => { setLayers(p => p.filter(l => l.id !== id)); if (activeLayerId === id) setActiveLayerId(null); };

  // ── Captions state ─────────────────────────────────────────────────────────
  const [cues, setCues] = useState([]);
  const [activeCueId, setActiveCueId] = useState(null);
  const activeCue = cues.find(c => c.id === activeCueId);

  const addCue = () => {
    const id = Date.now();
    const start = currentTime;
    const end = Math.min(start + 3, duration || start + 3);
    setCues(p => [...p, { id, start, end, text: 'Caption text' }]);
    setActiveCueId(id);
  };
  const updateCue = (id, k, v) => setCues(p => p.map(c => c.id === id ? { ...c, [k]: v } : c));
  const removeCue = (id) => { setCues(p => p.filter(c => c.id !== id)); if (activeCueId === id) setActiveCueId(null); };

  // ── Load existing VTT ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!existingVttUrl) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(existingVttUrl);
        if (!res.ok) return;
        const parsed = parseVTT(await res.text());
        if (alive && parsed.length) setCues(parsed);
      } catch (e) { console.error('Failed to load VTT:', e); }
    })();
    return () => { alive = false; };
  }, [existingVttUrl]);

  // ── Prevent body scroll ────────────────────────────────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Video events ───────────────────────────────────────────────────────────
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onMeta = () => {
      setDuration(vid.duration || 0);
      setTrimEnd(vid.duration || 0);
      setVideoReady(true);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    vid.addEventListener('loadedmetadata', onMeta);
    vid.addEventListener('play', onPlay);
    vid.addEventListener('pause', onPause);
    return () => {
      vid.removeEventListener('loadedmetadata', onMeta);
      vid.removeEventListener('play', onPlay);
      vid.removeEventListener('pause', onPause);
    };
  }, []);

  // ── Size canvas to container ───────────────────────────────────────────────
  useEffect(() => {
    if (!videoReady || !videoRef.current || !containerRef.current) return;
    const vid = videoRef.current;
    const el = containerRef.current;
    const maxW = el.clientWidth - 16;
    const maxH = window.innerHeight * 0.42;
    const ratio = vid.videoWidth / vid.videoHeight;
    let w = maxW, h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    setDispSize({ w: Math.round(w), h: Math.round(h) });
  }, [videoReady]);

  // ── Render loop: draw video + text overlays on canvas ──────────────────────
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const vid = videoRef.current;
    if (!canvas || !vid || !dispSize.w) return;

    canvas.width = dispSize.w;
    canvas.height = dispSize.h;
    const ctx = canvas.getContext('2d');

    // Apply filter + draw video frame
    ctx.filter = buildCSSFilter(adj);
    ctx.drawImage(vid, 0, 0, dispSize.w, dispSize.h);
    ctx.filter = 'none';

    const t = vid.currentTime;

    // Draw text layers visible at current time
    layers.forEach(layer => {
      if (t < layer.showStart || t > layer.showEnd) return;
      const font = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${layer.fontSize}px sans-serif`;
      ctx.font = font;
      ctx.textBaseline = 'top';
      if (layer.bgColor && layer.bgColor !== 'transparent') {
        const m = ctx.measureText(layer.text || ' ');
        const th = layer.fontSize * 1.3;
        ctx.fillStyle = layer.bgColor;
        ctx.fillRect(layer.x - 4, layer.y - 2, m.width + 8, th + 4);
      }
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = Math.max(2, layer.fontSize / 12);
      ctx.lineJoin = 'round';
      ctx.strokeText(layer.text || '', layer.x, layer.y);
      ctx.fillStyle = layer.color;
      ctx.fillText(layer.text || '', layer.x, layer.y);
      if (layer.id === activeLayerId) {
        const m = ctx.measureText(layer.text || ' ');
        const th = layer.fontSize * 1.3;
        ctx.strokeStyle = '#ff6800'; ctx.lineWidth = 2; ctx.setLineDash([5, 3]);
        ctx.strokeRect(layer.x - 6, layer.y - 4, m.width + 12, th + 8);
        ctx.setLineDash([]);
      }
    });

    // Draw active caption as subtitle overlay
    const currentCue = cues.find(c => t >= c.start && t <= c.end);
    if (currentCue) {
      const fontSize = Math.round(dispSize.h * 0.055);
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'center';
      const cx = dispSize.w / 2;
      const cy = dispSize.h - fontSize * 0.8;
      // Background
      const m = ctx.measureText(currentCue.text);
      ctx.fillStyle = 'rgba(0,0,0,0.75)';
      ctx.fillRect(cx - m.width / 2 - 8, cy - fontSize - 4, m.width + 16, fontSize + 12);
      // Text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(currentCue.text, cx, cy);
      ctx.textAlign = 'start';
    }
  }, [adj, layers, activeLayerId, cues, dispSize]);

  // Animation loop
  useEffect(() => {
    let running = true;
    const loop = () => {
      if (!running) return;
      const vid = videoRef.current;
      if (vid) {
        setCurrentTime(vid.currentTime);
        // Enforce trim bounds during playback
        if (playing && vid.currentTime >= trimEnd) {
          vid.pause();
          vid.currentTime = trimStart;
        }
      }
      redraw();
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => { running = false; cancelAnimationFrame(animRef.current); };
  }, [redraw, playing, trimStart, trimEnd]);

  // ── Playback controls ──────────────────────────────────────────────────────
  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      if (vid.currentTime < trimStart || vid.currentTime >= trimEnd) vid.currentTime = trimStart;
      vid.play();
    } else vid.pause();
  };

  const seekTo = useCallback((time) => {
    const vid = videoRef.current;
    if (vid) vid.currentTime = time;
  }, []);

  const stepBack = () => seekTo(Math.max(trimStart, currentTime - 1/30));
  const stepForward = () => seekTo(Math.min(trimEnd, currentTime + 1/30));

  // ── Canvas pointer events (text layer drag) ───────────────────────────────
  const canvasCoords = (e) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: cx, y: cy };
  };

  const handlePointerDown = (e) => {
    if (tab !== 'text') return;
    const { x, y } = canvasCoords(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i];
      ctx.font = `${l.italic ? 'italic ' : ''}${l.bold ? 'bold ' : ''}${l.fontSize}px sans-serif`;
      const m = ctx.measureText(l.text || ' ');
      const th = l.fontSize * 1.3;
      if (x >= l.x - 6 && x <= l.x + m.width + 6 && y >= l.y - 4 && y <= l.y + th + 4) {
        setActiveLayerId(l.id);
        setDraggingLayer({ id: l.id, ox: x - l.x, oy: y - l.y });
        return;
      }
    }
    setActiveLayerId(null);
  };

  const handlePointerMove = (e) => {
    if (!draggingLayer) return;
    e.preventDefault();
    const { x, y } = canvasCoords(e);
    updateLayer(draggingLayer.id, 'x', x - draggingLayer.ox);
    updateLayer(draggingLayer.id, 'y', y - draggingLayer.oy);
  };

  const handlePointerUp = () => setDraggingLayer(null);

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setBusy(true);
    setError('');

    try {
      const results = {};

      // 1. If captions exist, upload VTT
      if (cues.length > 0) {
        const vttText = generateVTT(cues);
        const vttBlob = new Blob([vttText], { type: 'text/vtt' });
        const vttFd = new FormData();
        vttFd.append('file', new File([vttBlob], `subtitles-${Date.now()}.vtt`, { type: 'text/vtt' }));
        if (tenantId) vttFd.append('tenantId', String(tenantId));
        if (eventId) vttFd.append('eventId', String(eventId));
        if (itemId) vttFd.append('itemId', String(itemId));

        const vttRes = await fetch('/api/media/upload', { method: 'POST', body: vttFd, credentials: 'include' });
        if (!vttRes.ok) throw new Error('VTT upload failed');
        const vttJson = await vttRes.json();
        results.vttUrl = vttJson.url;
      }

      // 2. If text overlays, filters, or trim changed, render a poster frame
      //    (Full re-encode requires server-side FFmpeg; we save the edit metadata
      //     and a representative frame so the user sees their edits)
      const hasEdits = layers.length > 0 ||
                       adj.brightness !== 1 || adj.contrast !== 1 ||
                       adj.saturate !== 1 || adj.sepia !== 0;

      if (hasEdits) {
        // Render current canvas frame as a thumbnail/poster
        const canvas = canvasRef.current;
        if (canvas) {
          const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.92));
          const fd = new FormData();
          fd.append('file', new File([blob], `video-poster-${Date.now()}.jpg`, { type: 'image/jpeg' }));
          if (tenantId) fd.append('tenantId', String(tenantId));
          if (eventId) fd.append('eventId', String(eventId));
          if (itemId) fd.append('itemId', String(itemId));

          const posterRes = await fetch('/api/media/upload', { method: 'POST', body: fd, credentials: 'include' });
          if (posterRes.ok) {
            const posterJson = await posterRes.json();
            results.posterUrl = posterJson.url;
          }
        }
      }

      // 3. Compile edit metadata (trim points, text overlays, filters)
      results.editMetadata = {
        trim: { start: trimStart, end: trimEnd },
        filters: adj,
        textOverlays: layers.map(l => ({
          text: l.text,
          x: l.x / dispSize.w, y: l.y / dispSize.h, // normalised
          fontSize: l.fontSize, color: l.color, bgColor: l.bgColor,
          bold: l.bold, italic: l.italic,
          showStart: l.showStart, showEnd: l.showEnd,
        })),
        captions: cues.map(c => ({ start: c.start, end: c.end, text: c.text })),
      };

      results.videoSrc = src;
      onSaved?.(results);
      onClose?.();
    } catch (err) {
      console.error('VideoEditor save failed:', err);
      setError(err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Reset ──────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setAdj({ brightness: 1, contrast: 1, saturate: 1, sepia: 0 });
    setTrimStart(0);
    setTrimEnd(duration);
    setLayers([]);
    setActiveLayerId(null);
    setCues([]);
    setActiveCueId(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ve-overlay">
      {/* Header */}
      <div className="ve-header">
        <button className="ve-hdr-btn" onClick={onClose} disabled={busy}><FaTimes /></button>
        <span className="ve-hdr-title">Edit Video</span>
        <div className="ve-hdr-right">
          <button className="ve-hdr-btn" onClick={handleReset} disabled={busy} title="Reset all">
            <FaArrowLeft /> Reset
          </button>
          <button className="ve-hdr-btn ve-hdr-save" onClick={handleSave} disabled={busy}>
            <FaCheck /> {busy ? 'Saving…' : 'Done'}
          </button>
        </div>
      </div>

      {error && <div className="ve-error">{error}</div>}

      {/* Hidden video element (source for canvas) */}
      <video
        ref={videoRef}
        src={src}
        crossOrigin="anonymous"
        playsInline
        muted={false}
        preload="auto"
        style={{ display: 'none' }}
      />

      {/* Canvas preview */}
      <div className="ve-stage" ref={containerRef}>
        {!videoReady ? (
          <div className="ve-loading">Loading video…</div>
        ) : (
          <canvas
            ref={canvasRef}
            className="ve-canvas"
            style={{ width: dispSize.w, height: dispSize.h }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
        )}
      </div>

      {/* Transport controls */}
      <div className="ve-transport">
        <button className="ve-transport-btn ve-transport-sm" onClick={stepBack} title="Step back">
          <FaStepBackward />
        </button>
        <button className="ve-transport-btn" onClick={togglePlay}>
          {playing ? <FaPause /> : <FaPlay />}
        </button>
        <button className="ve-transport-btn ve-transport-sm" onClick={stepForward} title="Step forward">
          <FaStepForward />
        </button>
        <input
          type="range"
          className="ve-scrubber"
          min={0}
          max={duration || 1}
          step={0.01}
          value={currentTime}
          onChange={(e) => seekTo(Number(e.target.value))}
        />
        <span className="ve-time">{fmt(currentTime)} / {fmt(duration)}</span>
      </div>

      {/* Tab bar */}
      <div className="ve-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`ve-tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'trim' && <FaCut />}
            {t === 'text' && <FaFont />}
            {t === 'captions' && <FaClosedCaptioning />}
            {t === 'adjust' && <FaSun />}
            <span>{t === 'captions' ? 'CC' : t.charAt(0).toUpperCase() + t.slice(1)}</span>
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div className="ve-panel">
        {/* ─── TRIM ─── */}
        {tab === 'trim' && (
          <div className="ve-trim-panel">
            <div className="ve-trim-row">
              <label>Start</label>
              <span className="ve-trim-time">{fmt(trimStart)}</span>
              <button className="ve-btn-set" onClick={() => setTrimStart(currentTime)}>Set to playhead</button>
            </div>
            <div className="ve-trim-row">
              <label>End</label>
              <span className="ve-trim-time">{fmt(trimEnd)}</span>
              <button className="ve-btn-set" onClick={() => setTrimEnd(currentTime)}>Set to playhead</button>
            </div>
            {/* Visual trim bar */}
            <div className="ve-trim-bar">
              <div
                className="ve-trim-region"
                style={{
                  left: `${(trimStart / (duration || 1)) * 100}%`,
                  width: `${((trimEnd - trimStart) / (duration || 1)) * 100}%`,
                }}
              />
              <div
                className="ve-trim-playhead"
                style={{ left: `${(currentTime / (duration || 1)) * 100}%` }}
              />
            </div>
            <div className="ve-trim-info">
              Duration: {fmt(trimEnd - trimStart)}
            </div>
          </div>
        )}

        {/* ─── TEXT OVERLAY ─── */}
        {tab === 'text' && (
          <div className="ve-text-panel">
            <button className="ve-btn-add" onClick={addTextLayer}><FaPlus /> Add Text</button>
            {activeLayer && (
              <div className="ve-text-controls">
                <div className="ve-text-row">
                  <MentionInput
                    singleLine
                    className="ve-text-input"
                    value={activeLayer.text}
                    onChange={val => updateLayer(activeLayer.id, 'text', val)}
                    placeholder="Enter text… type @ to tag"
                    dropdownPosition="above"
                  />
                  <button className="ve-btn-del" onClick={() => removeLayer(activeLayer.id)}><FaTrash /></button>
                </div>
                <div className="ve-text-row">
                  <label>Size</label>
                  <input type="range" min={12} max={80} value={activeLayer.fontSize}
                    onChange={e => updateLayer(activeLayer.id, 'fontSize', Number(e.target.value))} />
                  <span>{activeLayer.fontSize}px</span>
                </div>
                <div className="ve-text-row">
                  <input type="color" value={activeLayer.color}
                    onChange={e => updateLayer(activeLayer.id, 'color', e.target.value)} className="ve-color" />
                  <select value={activeLayer.bgColor}
                    onChange={e => updateLayer(activeLayer.id, 'bgColor', e.target.value)} className="ve-select">
                    <option value="transparent">No BG</option>
                    <option value="rgba(0,0,0,0.5)">Dark</option>
                    <option value="rgba(255,255,255,0.7)">Light</option>
                    <option value="rgba(0,0,0,0.85)">Solid</option>
                  </select>
                  <button className={`ve-toggle${activeLayer.bold ? ' active' : ''}`}
                    onClick={() => updateLayer(activeLayer.id, 'bold', !activeLayer.bold)}><FaBold /></button>
                  <button className={`ve-toggle${activeLayer.italic ? ' active' : ''}`}
                    onClick={() => updateLayer(activeLayer.id, 'italic', !activeLayer.italic)}><FaItalic /></button>
                </div>
                <div className="ve-text-row">
                  <label>Show</label>
                  <span className="ve-trim-time">{fmt(activeLayer.showStart)}</span>
                  <button className="ve-btn-set" onClick={() => updateLayer(activeLayer.id, 'showStart', currentTime)}>
                    Set start
                  </button>
                  <span className="ve-trim-time">{fmt(activeLayer.showEnd)}</span>
                  <button className="ve-btn-set" onClick={() => updateLayer(activeLayer.id, 'showEnd', currentTime)}>
                    Set end
                  </button>
                </div>
              </div>
            )}
            {layers.length > 0 && <div className="ve-hint">Drag text on video to reposition</div>}
          </div>
        )}

        {/* ─── CAPTIONS (CC) ─── */}
        {tab === 'captions' && (
          <div className="ve-cc-panel">
            <div className="ve-cc-header">
              <button className="ve-btn-add" onClick={addCue}>
                <FaPlus /> Add Caption at {fmt(currentTime)}
              </button>
              <span className="ve-cue-count">{cues.length} cue{cues.length !== 1 ? 's' : ''}</span>
            </div>

            {activeCue && (
              <div className="ve-cue-editor">
                <div className="ve-cue-row">
                  <label>Text</label>
                  <textarea
                    className="ve-cue-input"
                    value={activeCue.text}
                    onChange={e => updateCue(activeCue.id, 'text', e.target.value)}
                    rows={2}
                    placeholder="Caption text..."
                  />
                </div>
                <div className="ve-cue-row">
                  <label>Start</label>
                  <span className="ve-trim-time">{fmt(activeCue.start)}</span>
                  <button className="ve-btn-set" onClick={() => updateCue(activeCue.id, 'start', currentTime)}>Set</button>
                  <label>End</label>
                  <span className="ve-trim-time">{fmt(activeCue.end)}</span>
                  <button className="ve-btn-set" onClick={() => updateCue(activeCue.id, 'end', currentTime)}>Set</button>
                  <button className="ve-btn-del" onClick={() => removeCue(activeCue.id)}><FaTrash /></button>
                </div>
              </div>
            )}

            <div className="ve-cue-list">
              {cues.slice().sort((a, b) => a.start - b.start).map(cue => {
                const isCurrent = currentTime >= cue.start && currentTime <= cue.end;
                return (
                  <button
                    key={cue.id}
                    className={`ve-cue-item${cue.id === activeCueId ? ' active' : ''}${isCurrent ? ' playing' : ''}`}
                    onClick={() => { setActiveCueId(cue.id); seekTo(cue.start); }}
                  >
                    <span className="ve-cue-time">{fmt(cue.start)} → {fmt(cue.end)}</span>
                    <span className="ve-cue-text">{cue.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── ADJUST (FILTERS) ─── */}
        {tab === 'adjust' && (
          <div className="ve-sliders">
            {[
              { key: 'brightness', label: 'Brightness', min: 0.3, max: 1.7, step: 0.01, dflt: 1 },
              { key: 'contrast',   label: 'Contrast',   min: 0.3, max: 2,   step: 0.01, dflt: 1 },
              { key: 'saturate',   label: 'Saturation', min: 0,   max: 2,   step: 0.01, dflt: 1 },
              { key: 'sepia',      label: 'Warmth',     min: 0,   max: 0.8, step: 0.01, dflt: 0 },
            ].map(s => (
              <div className="ve-slider-row" key={s.key}>
                <label>{s.label}</label>
                <input
                  type="range" min={s.min} max={s.max} step={s.step}
                  value={adj[s.key]}
                  onChange={e => updateAdj(s.key, Number(e.target.value))}
                />
                <span className="ve-slider-val">{adj[s.key].toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
